import { useIsFetching } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigation } from "react-router-dom";
import { cn } from "../tailwind-utils";
import { useTranslations } from "../contexts/I18nContext";

const LOADING_BAR_SHOW_DELAY_MS = 120;
const LOADING_BAR_HIDE_DELAY_MS = 180;

export const RouteLoadingBar: React.FC = () => {
  const i18n = useTranslations();
  const location = useLocation();
  const navigation = useNavigation();
  const isFetching = useIsFetching();
  const previousPathRef = useRef(location.pathname);
  const showTimeoutRef = useRef<number | null>(null);
  const hideTimeoutRef = useRef<number | null>(null);
  const [isRouteTransitioning, setIsRouteTransitioning] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (previousPathRef.current === location.pathname) return;
    previousPathRef.current = location.pathname;
    setIsRouteTransitioning(true);
    const timeout = window.setTimeout(() => {
      setIsRouteTransitioning(false);
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [location.pathname]);

  const isLoading =
    navigation.state !== "idle" || isFetching > 0 || isRouteTransitioning;

  useEffect(() => {
    if (isLoading) {
      if (hideTimeoutRef.current) {
        window.clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      if (showTimeoutRef.current) {
        window.clearTimeout(showTimeoutRef.current);
      }
      showTimeoutRef.current = window.setTimeout(() => {
        setIsVisible(true);
        showTimeoutRef.current = null;
      }, LOADING_BAR_SHOW_DELAY_MS);
      return;
    }

    if (showTimeoutRef.current) {
      window.clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    if (hideTimeoutRef.current) {
      window.clearTimeout(hideTimeoutRef.current);
    }
    hideTimeoutRef.current = window.setTimeout(() => {
      setIsVisible(false);
      hideTimeoutRef.current = null;
    }, LOADING_BAR_HIDE_DELAY_MS);
  }, [isLoading]);

  useEffect(
    () => () => {
      if (showTimeoutRef.current) {
        window.clearTimeout(showTimeoutRef.current);
      }
      if (hideTimeoutRef.current) {
        window.clearTimeout(hideTimeoutRef.current);
      }
    },
    []
  );

  return (
    <>
      <div
        aria-hidden
        className={cn(
          "pointer-events-none fixed inset-x-0 top-0 z-[100] h-1 transition-opacity duration-200",
          isVisible ? "opacity-100" : "opacity-0"
        )}
      >
        <div
          className={cn(
            "h-full bg-blue-500 transition-all duration-300 ease-out",
            isLoading ? "w-2/3" : "w-full"
          )}
        />
      </div>
      <span className="sr-only" role="status" aria-live="polite">
        {isLoading ? i18n["Loading..."] : i18n.Loaded}
      </span>
    </>
  );
};
